<template>
	<div class="d3-geofencing-timeline">
		<div class="container" ref="container">
			<svg class="geofencing-timeline" :width="container.width" :height="container.height">
				<line class="ruler" x1="0" :y1="container.height / 2" :x2="container.width" :y2="container.height / 2" :style="lineRulerStyle"/>
				<g class="axis"></g>
				<g class="alerts" :transform="`translate(0, ${container.height / 4})`"></g>
				<g class="groups" :transform="`translate(0, ${container.height / 2})`"></g>
				<g class="readings" :transform="`translate(0, ${container.height / 2})`"></g>
				<g class="voronoi"></g>
				<g class="slider">
					<circle r="4" cx="0" :cy="container.height / 2"></circle>
					<line x1="0" y1="0" x2="0" :y2="container.height"/>
				</g>
				<g class="hour">
					<image x="0" y="0" width="50" height="50" xlink:href="/static/svg/maps/pin-hour.svg"/>
					<text x="25" y="34" text-anchor="middle" font-size="14px">text</text>
				</g>
			</svg>
		</div>
	</div>
</template>

<script>
import clamp from '@/utils/clamp'
import * as d3 from '@/utils/d3'
export default {
	props: [
		'readings',
		'alerts',
		'intervalStart',
		'intervalEnd'
	],
	data () {
		return {
			container: {
				width: 0,
				height: 0
			},
			selected: null,
			hover:  null
		}
	},
	computed: {
		date () {
			return this.intervalStart ? new Date(this.intervalStart) : new Date()
		},
		scale () {
			const start = this.date.setHours(0, 0, 0, 0)
			const end = this.date.setHours(24, 0, 0, 0)
			return d3.scaleTime()
				.domain([start, end])
				.range([0, this.container.width])
		},
		readingGroups () {
			const minDiff = 60 * 60 * 1000 // 60 min
			let flag = false // flag to store if current element is already grouped
			const groups = this.readings.reduce((acc, cur) => {
					flag = false // reset flag
					acc.some(group => {
						group.some(element => {
							if (Math.abs(this.$moment(cur.datetime).diff(this.$moment(element.datetime))) <= minDiff) {
								group.push(cur)
								flag = true // is grouped, proceed to break out of loops
							}
							return flag
						})
						return flag
					})
					// if still not grouped, create new group with current element
					if (!flag) acc.push([cur]) 
					return acc
			}, [])
			groups.forEach(group => group.sort((a, b) => this.$moment(a.datetime) > this.$moment(b.datetime)))
			return groups
		},
		// Css style
		lineRulerStyle () {
			const fill = 10
			const space = 4
			return {
				strokeDasharray: `${fill} ${space}`
			}
		}
	},
	watch: {
		readings () {
			this.selected = null
			this.hover = null
			this.onResize()
		},
		alerts () {
			this.onResize()
		},
		selected () {
			this.$emit('selected', this.selected)
			//console.log(this.selected)
		}
	},
	methods: {
		onResize () {
			this.container.width = Math.max(this.$refs.container.offsetWidth, 0)
			this.container.height = 50
			this.update()
		},
		update () {
			// SVG
			const svg = d3.select('svg.geofencing-timeline')

			// Axis
			const axis = d3.axisTop(this.scale)
				.tickSize(-50)
				//.tickPadding(50)
				//.tickValues([1, 2])
				//.tickFormat(n => n)
				.tickFormat(d3.timeFormat('%-Hh'))

			d3.select('svg.geofencing-timeline .axis')
				.call(axis)

			// Alerts dots
			const alerts = d3.select('svg.geofencing-timeline .alerts')
				.selectAll('.alert')
				.data(this.alerts)

			alerts.enter()
				.append('circle')
				.merge(alerts)
				.attr('class', 'alert')
				.attr('r', 4)
				.attr('cx', d => this.scale(new Date(d.date)))

				alerts.exit()
					.remove()

			// Readings groups
			const groups = d3.select('svg.geofencing-timeline .groups')
				.selectAll('.group')
				.data(this.readingGroups)

			groups.enter()
				.append('line')
				.merge(groups)
				.attr('class', 'group')
				.attr('x1', d => this.scale(new Date(d[0].datetime)))
				.attr('y1', 0)
				.attr('x2', d => this.scale(new Date(d[d.length - 1].datetime)))
				.attr('y2', 0)
			
			groups.exit()
				.remove()

			// Readings dots
			const dots = d3.select('svg.geofencing-timeline .readings')
				.selectAll('.reading')
				.data(this.readings)

			dots.enter()
				.append('circle')
				.merge(dots)
				.attr('class', 'reading')
				.attr('r', 4)
				.attr('cx', d => this.scale(new Date(d.datetime)))
				.on('click', (d, i, nodes) => {
					d3.event.stopPropagation()
					this.selectReading(d, nodes[i])
				})

			dots.exit()
				.remove()

			// Slider grab
			d3.select('svg.geofencing-timeline .slider')
				.classed('active', false)
				.call(d3.drag()
					.on('start', (d, i, nodes) => {
						d3.select(nodes[i]).raise()
					})
					.on('drag', (d, i, nodes) => {
						const x = clamp(d3.event.x, 0, this.container.width)
						d3.select(nodes[i])
							.attr('transform', `translate(${x}, 0)`)
						d3.select('.hour')
							.attr('transform', `translate(${x - 25}, 40)`)
							.select('text')
							.text(() => this.$moment(this.scale.invert(x)).format('HH:mm'))

						const neighbors = this.calculate2NearestTimeNeighbors(d3.event.x)
						this.$emit('neighbors', neighbors)
					})
					.on('end', () => {
						//console.log('end')
					}))

			// Slider hour
			d3.select('svg.geofencing-timeline .hour')
				.classed('active', false)

			// Voronoi
			const voronoi = d3.voronoi()
				.extent([[0, 0], [this.container.width, this.container.height]])
				.x(d => this.scale(new Date(d.datetime)))
				.y(d => 0)

			const points = this.readings
			const diagram = voronoi(points)
			const polygons = diagram.polygons()

			const voronoiGroup = d3.select('svg.geofencing-timeline .voronoi')
				.selectAll('path')
				.data(polygons)

			voronoiGroup.enter()
				.append('path')
				.merge(voronoiGroup)
				.attr('d', d => d ? "M" + d.join("L") + "Z" : null)

			voronoiGroup.exit()
				.remove()

			// Detect reading under mouse with help of voronoi diagram
			/*svg.on('mousemove', () => {
				const mouse = d3.mouse(svg.node())
				const site = diagram.find(mouse[0], mouse[1])
				if (site && site.data) {
					this.hover = site.data
				}
			})*/

			// Select nearest reading on click with help of voronoi diagram
			svg.on('click', () => {
				const mouse = d3.mouse(svg.node())
				const site = diagram.find(mouse[0], mouse[1])
				if (site && site.data) {
					const selection = d3.selectAll('svg.geofencing-timeline .reading')
						.filter(d => d.id === site.data.id)
					this.selectReading(site.data, selection.node())
				}
			})
		},
		selectReading (reading, node) {
			d3.selectAll('.reading').classed('selected', false)
			d3.select(node).raise().classed('selected', true)
			d3.select('.slider').raise().classed('active', true)
				.attr('transform', `translate(${node.getAttribute('cx')}, 0)`)
			d3.select('.hour').classed('active', true)
				.attr('transform', `translate(${node.getAttribute('cx') - 25}, 30)`)
				.select('text')
				.text(() => this.$moment(this.scale.invert(node.getAttribute('cx'))).format('HH:mm'))

			this.selected = reading
		},
		calculate2NearestTimeNeighbors (value) {
			const neighbors = []
			const temp = []
			const a = value
			let b
			let c

			this.readings.forEach(element => {
				b = this.scale(new Date(element.datetime))
				c = b - a
				temp.push([element.id, c])
			})

			const sortedValues = temp.sort((a, b) => a[1] > b[1])
			const greaterThan = []
			const lessThan = []
			sortedValues.forEach(element => {
				if (element[1] < 0) lessThan.push(element)
				if (element[1] > 0) greaterThan.push(element)
			})
			if (lessThan.length) neighbors.push(lessThan[lessThan.length - 1])
			if (greaterThan.length) neighbors.push(greaterThan[0])

			return neighbors
		}
	},
	mounted () {
		window.addEventListener('resize', this.onResize)
		this.onResize()
	},
	beforeDestroy () {
		window.removeEventListener('resize', this.onResize)
	}
}
</script>



// WEBPACK FOOTER //
// src/components/watch/readings/geofencing/widgets/history/GeofencingWidgetHistoryMapTimeline.vue